import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { bp } from 'Styles/helpers';
import { rem } from 'polished';
import screen from 'superior-mq';
import objectPath from 'object-path';
import Grid from '../grid';
import { Link } from '../../routes';
import StaggerIn from '../stagger-in';

const gridStyles = `
  margin-top: 60px;
  grid-gap: 50px 30px;

  ${screen.above(bp.tablet, `
    margin-top: 120px;
    grid-gap: 84px;
  `)};

  ${screen.below(bp.tablet, `
    grid-template-columns: repeat(2, 1fr);
  `)};

  ${screen.below(bp.mobileSm, `
    grid-gap: 50px 30px;
  `)};
`;

const gridItemStyles = `
  grid-column: span 3;

  ${screen.below(bp.tablet, `
    grid-column: span 1;
  `)};
`;

const PartnerGrid = ({ partnerList, isDark }) => {
  if (partnerList.length < 1) return null;

  return (
    <StaggerIn>
      <Grid cols={12} styles={gridStyles}>

        {partnerList.map(partner => (
          <Grid.Item key={partner.sys.id} styles={gridItemStyles} className="js-stagger">
            <Link route={`/partners/${partner.fields.partnerSlug}`} passHref>
              <PartnerLink>
                <Partner>

                  <LogoWrap>
                    {isDark && objectPath.has(partner, 'fields.partnerLogoLight.fields.file.url') &&
                      <Logo
                        src={`${partner.fields.partnerLogoLight.fields.file.url}?q=80`}
                        alt={`${partner.fields.partnerName} logo`}
                      />
                    }

                    {!isDark && objectPath.has(partner, 'fields.partnerLogo.fields.file.url') &&
                      <Logo
                        src={`${partner.fields.partnerLogo.fields.file.url}?q=80`}
                        alt={`${partner.fields.partnerName} logo`}
                      />
                    }
                  </LogoWrap>

                  <Title>{partner.fields.featuredProjectName}</Title>

                  <Description>{partner.fields.caseStudyDescription}</Description>

                  <CaseStudyText>View Initiative</CaseStudyText>

                </Partner>
              </PartnerLink>
            </Link>
          </Grid.Item>
        ))}

      </Grid>
    </StaggerIn>
  );
};

const Partner = styled.div`
  text-align: left;

  img {
    display: block;
    margin: auto;

    ${screen.below(bp.mobile, `
      max-height: 64px;
    `)};
  }
`;

const LogoWrap = styled.div`
  position: relative;
  max-width: 150px;
  margin: auto;

  &::after {
    content: "";
    padding-bottom: ${(140 / 240) * 100}%;
    display: block;
  }
`;

const Logo = styled.img`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  max-height: 100%;
  transform: translate(-50%, -50%);

  ${screen.below(bp.laptop, `
    max-width: 80%;
  `)}

  ${screen.below(bp.laptopSm, `
    max-width: 90%;
  `)}
`;

const PartnerLink = styled.a`
  display: block;
  color: unset;
  text-decoration: none;
`;

const Title = styled.h4`
  padding-top: 24px;
  margin-top: 50px;
  font-family: ${props => props.theme.sansFont};
  font-size: ${rem(18)};
  border-top: 5px solid ${props => props.theme.blue};

  ${screen.below(bp.mobile, `
    margin-top: 30px;
    font-size: ${rem(15)};
  `)};
`;

const Description = styled.p`
  margin-top: 64px;
  font-size: ${rem(16)};

  ${screen.below(bp.mobile, `
    margin-top: 20px;
    margin-bottom: 0;
    font-size: ${rem(12)};
  `)};
`;

const CaseStudyText = styled.span`
  color: ${props => props.theme.blue};
  text-decoration: underline;
  font-size: ${rem(16)};

  ${screen.below(bp.mobile, `
    font-size: ${rem(12)};
  `)}
`;

PartnerGrid.propTypes = {
  partnerList: PropTypes.array,
  isDark: PropTypes.bool,
};

PartnerGrid.defaultProps = {
  partnerList: [],
};

export default PartnerGrid;
